import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Download, Calendar, TrendingUp, CheckCircle,
  AlertTriangle, ArrowRight, BarChart3, DollarSign, Shield
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import { useBranding } from '@/context/BrandingContext';
import { useCurrency } from '@/context/CurrencyContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';
import { Input } from '@/components/ui/input';
import InfoTip from '@/components/InfoTip';

const Reports = () => {
  const [month, setMonth] = useState('feb-2025');
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().split('T')[0]; });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [department, setDepartment] = useState('all');
  const [person, setPerson] = useState('all');
  const [bomFilter, setBomFilter] = useState('all');
  const { branding } = useBranding();
  const { format } = useCurrency();

  const monthlySummary = {
    suggestionsGenerated: 48,
    actionsTaken: 22,
    savingsRealized: 67500,
    risksIdentified: 8,
    risksMitigated: 5,
    bomsAnalyzed: 12,
  };

  const actionBreakdown = [
    { category: 'Negotiations', count: 8, savings: 24500 },
    { category: 'Alternates', count: 6, savings: 18200 },
    { category: 'Risk Mitigation', count: 5, savings: 15800 },
    { category: 'Volume Optimization', count: 3, savings: 9000 },
  ];

  const trendData = [
    { month: 'Sep', suggestions: 32, actions: 12, savings: 42000 },
    { month: 'Oct', suggestions: 38, actions: 16, savings: 52000 },
    { month: 'Nov', suggestions: 42, actions: 19, savings: 58000 },
    { month: 'Dec', suggestions: 45, actions: 20, savings: 62000 },
    { month: 'Jan', suggestions: 50, actions: 24, savings: 72000 },
    { month: 'Feb', suggestions: 48, actions: 22, savings: 67500 },
  ];

  const COLORS = ['#16a34a', '#0ea5e9', '#f59e0b', '#8b5cf6'];

  return (
    <Layout title="Reports" subtitle="Monthly summaries of suggestions, actions, and results">
      <div className="space-y-6" data-testid="reports-page">
        {/* Month Selector */}
        <div className="flex items-center justify-between">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-52" data-testid="report-month-select">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="feb-2025">February 2025</SelectItem>
              <SelectItem value="jan-2025">January 2025</SelectItem>
              <SelectItem value="dec-2024">December 2024</SelectItem>
              <SelectItem value="nov-2024">November 2024</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => {
            const doc = createBrandedPDF('Monthly Report', `Report for ${month}`, branding);
            let y = doc._startY;
            y = addSectionTitle(doc, 'Monthly Summary', y);
            y = addMetricCards(doc, [
              { label: 'Suggestions', value: String(monthlySummary.suggestionsGenerated) },
              { label: 'Actions Taken', value: String(monthlySummary.actionsTaken) },
              { label: 'Savings', value: `$${(monthlySummary.savingsRealized / 1000).toFixed(1)}K` },
              { label: 'Risks Found', value: String(monthlySummary.risksIdentified) },
            ], y);
            y = addSectionTitle(doc, 'Action Breakdown', y);
            y = addTable(doc, ['Category', 'Actions', 'Savings'],
              actionBreakdown.map(a => [a.category, String(a.count), `$${a.savings.toLocaleString()}`]), y);
            y = addSectionTitle(doc, 'Monthly Trend', y);
            y = addTable(doc, ['Month', 'Suggestions', 'Actions', 'Savings'],
              trendData.map(t => [t.month, String(t.suggestions), String(t.actions), `$${t.savings.toLocaleString()}`]), y);
            downloadPDF(doc, `report-${month}.pdf`);
            toast.success('PDF downloaded');
          }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="export-report-btn">
            <Download className="h-4 w-4 mr-2" /> Export Report
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <BarChart3 className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{monthlySummary.suggestionsGenerated}</p>
              <p className="text-xs text-gray-500 mt-1">Suggestions</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{monthlySummary.actionsTaken}</p>
              <p className="text-xs text-gray-500 mt-1">Actions Taken</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <DollarSign className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">${(monthlySummary.savingsRealized / 1000).toFixed(1)}K</p>
              <p className="text-xs text-gray-500 mt-1">Savings Realized</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-600">{monthlySummary.risksIdentified}</p>
              <p className="text-xs text-gray-500 mt-1">Risks Found</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Shield className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{monthlySummary.risksMitigated}</p>
              <p className="text-xs text-gray-500 mt-1">Risks Mitigated</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6 text-center">
              <FileText className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-600">{monthlySummary.bomsAnalyzed}</p>
              <p className="text-xs text-gray-500 mt-1">BOMs Analyzed</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="actions" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="actions">Action Breakdown</TabsTrigger>
            <TabsTrigger value="trend">Monthly Trend</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
          </TabsList>

          <TabsContent value="actions" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-gray-900">Actions by Category</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {actionBreakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg" data-testid={`action-breakdown-${idx}`}>
                      <div className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[idx]}} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">{item.category}</span>
                          <span className="text-sm text-gray-500">{item.count} actions</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full" style={{width: `${(item.savings / 25000) * 100}%`, backgroundColor: COLORS[idx]}} />
                        </div>
                      </div>
                      <span className="font-bold text-green-600 w-20 text-right">${item.savings.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trend" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-gray-900">Monthly Performance Trend</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v, name) => name === 'savings' ? `$${v.toLocaleString()}` : v} />
                    <Bar yAxisId="left" dataKey="suggestions" fill="#93c5fd" name="Suggestions" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="actions" fill="#16a34a" name="Actions" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="distribution" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-gray-900">Savings Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={actionBreakdown} dataKey="savings" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={({category, percent}) => `${category}: ${(percent * 100).toFixed(0)}%`}>
                        {actionBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
